import { useState, useEffect } from 'react';

import * as api from '../utils/api';
import { getUserInfo } from '../utils/helpers';
import { useAppDispatch } from '../redux/hooks';
import { updateTickers } from '../layout/AppInfoSlice';
import useReportForm from './validations/useReportForm';
import useFeedbackForm from './validations/useFeedbackForm';
import useSupportForm from './validations/useSupportForm';

const useHeader = () => {
  const dispatch = useAppDispatch();
  const [isAccountInformationLoading, setAccountInformationLoading] = useState(
    true
  );
  const [accountInformationData, setAccountInformationData] = useState(null);
  const [isSubmitReportFormLoading, setSubmitReportFormLoading] = useState(
    false
  );
  const [isSubmitFeedbackFormLoading, setSubmitFeedbackFormLoading] = useState(
    false
  );
  const [isSubmitSupportFormLoading, setSubmitSupportFormLoading] = useState(
    false
  );
  const [saveMessage, setSaveMessage] = useState('');
  const [submitMessage, setSubmitMessage] = useState({
    title: '',
    message: '',
  });
  const [selectedModal, setSelectedModal] = useState('');
  const [listCompanies, setListCompanies] = useState([]);
  const [openInstructionalVideo, setInstructionalVideo] = useState(false);
  const [tickers, setTickers] = useState([]);

  const getListCompanies = async () => {
    const companies = [];
    try {
      const results = await api.get('/stock-exchange/all');
      for (const item of results.data) {
        companies.push({
          value: item.name,
          label: item.name,
        });
      }

      const tickerResults = await api.get('/futures-insight-ticker/all');
      for (const item of tickerResults.data) {
        companies.push({
          value: item.ticker,
          label: item.ticker,
        });
      }
      if (tickerResults?.data?.length) {
        const _tickers = tickerResults.data.map((d) => d.ticker);
        setTickers(_tickers);
        dispatch(updateTickers(_tickers));
      }

      const { data } = await api.get('/company/ma-chung-khoan');
      for (const c of data) {
        companies.push({
          value: c.machungkhoan,
          label: `${c.machungkhoan} - ${c.name}`,
        });
      }

      setListCompanies(companies);
    } catch (error) {
      console.error('Error fetching list of companies:', error);
    }
  };

  const fetchAccountInformationData = async () => {
    setAccountInformationLoading(true);
    try {
      const { data } = await api.get(`/user/me`);
      setAccountInformationData(getUserInfo(data));
    } catch (error) {
      console.error(error);
    }
    setAccountInformationLoading(false);
  };

  const handleSubmitReportForm = async (values) => {
    try {
      setSubmitReportFormLoading(true);
      const { data } = await api.post('/help-desk/report-data', values);
      if (data.status) {
        setSubmitMessage({
          title: 'Báo lỗi dữ liệu',
          message: 'Cảm ơn bạn đã thông báo lỗi cho StockUp.vn',
        });
        setSelectedModal('finish');
      } else {
        setSaveMessage(
          'Có lỗi xảy ra trong quá trình gửi dữ liệu, xin thử lại sau!'
        );
      }
    } catch (error) {
      setSaveMessage(
        'Có lỗi xảy ra trong quá trình gửi dữ liệu, xin thử lại sau!'
      );
    }
    setSubmitReportFormLoading(false);
  };

  const handleCloseReportModal = () => {
    if (!isSubmitReportFormLoading) {
      reportForm.formik.resetForm();
      setSelectedModal('');
    }
  };

  const reportForm = useReportForm({
    handleSubmit: handleSubmitReportForm,
  });

  const handleSubmitFeedbackForm = async (values) => {
    try {
      setSubmitFeedbackFormLoading(true);
      const { data } = await api.post('/help-desk/feedback', values);
      if (data.status) {
        setSubmitMessage({
          title: 'Góp ý',
          message: 'Cảm ơn bạn đã gửi góp ý cho StockUp.vn',
        });
        setSelectedModal('finish');
      } else {
        setSaveMessage(
          'Có lỗi xảy ra trong quá trình gửi dữ liệu, xin thử lại sau!'
        );
      }
    } catch (error) {
      setSaveMessage(
        'Có lỗi xảy ra trong quá trình gửi dữ liệu, xin thử lại sau!'
      );
    }
    setSubmitFeedbackFormLoading(false);
  };

  const handleCloseFeedbackModal = () => {
    if (!isSubmitFeedbackFormLoading) {
      feedBackForm.formik.resetForm();
      setSelectedModal('');
    }
  };

  const feedBackForm = useFeedbackForm({
    handleSubmit: handleSubmitFeedbackForm,
  });

  const handleSubmitSupportForm = async (values) => {
    try {
      setSubmitSupportFormLoading(true);
      const { data } = await api.post('/help-desk/support', values);
      if (data.status) {
        setSubmitMessage({
          title: 'Hỗ trợ',
          message: 'Cảm ơn bạn đã gửi thông tin cho StockUp.vn',
        });
        setSelectedModal('finish');
      } else {
        setSaveMessage(
          'Có lỗi xảy ra trong quá trình gửi dữ liệu, xin thử lại sau!'
        );
      }
      setSubmitSupportFormLoading(false);
    } catch (error) {
      setSaveMessage(
        'Có lỗi xảy ra trong quá trình gửi dữ liệu, xin thử lại sau!'
      );
    }
    setSubmitSupportFormLoading(false);
  };

  const handleCloseSupportModal = () => {
    if (!isSubmitSupportFormLoading) {
      supportForm.formik.resetForm();
      setSelectedModal('');
    }
  };

  const supportForm = useSupportForm({
    handleSubmit: handleSubmitSupportForm,
  });

  const handleCloseFinishModal = () => {
    reportForm.formik.resetForm();
    supportForm.formik.resetForm();
    feedBackForm.formik.resetForm();
    setSubmitMessage('');
    setSelectedModal('');
  };

  useEffect(() => {
    fetchAccountInformationData();
    getListCompanies();
  }, []);

  const handleOpenInstructionalVideo = () => {
    setInstructionalVideo(true);
  };

  const handleCloseInstructionalVideo = () => {
    setInstructionalVideo(false);
  };

  const saveNotificationOptions = async (notificationOptions) => {
    try {
      await api.post(`/user/save-notification-options`, {
        notificationOptions: JSON.stringify(notificationOptions),
      });
      setAccountInformationData({
        ...accountInformationData,
        notificationOptions: JSON.stringify(notificationOptions),
      });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    tickers,
    accountInformationData,
    isSubmitReportFormLoading,
    reportFormik: reportForm.formik,
    handleCloseReportModal,
    isSubmitFeedbackFormLoading,
    feedbackFormik: feedBackForm.formik,
    handleCloseFeedbackModal,
    isSubmitSupportFormLoading,
    supportFormik: supportForm.formik,
    handleCloseSupportModal,
    saveMessage,
    submitMessage,
    selectedModal,
    setSelectedModal,
    handleCloseFinishModal,
    listCompanies,
    openInstructionalVideo,
    handleCloseInstructionalVideo,
    handleOpenInstructionalVideo,
    saveNotificationOptions,
    isAccountInformationLoading,
  };
};

export default useHeader;
